import type { BookingKind, Locale, RoomSelection, TreatmentDuration } from "../types";

const BOOKING_API_URL = import.meta.env.VITE_BOOKING_API_URL?.trim();
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

export const BOOKING_POLICY_VERSION = "late-arrival-v1-2026-09-20";

export type ReserveBookingInput = {
  requestId: string;
  bookingKind: BookingKind;
  duration: TreatmentDuration;
  date: string;
  time: string;
  room: RoomSelection;
  name: string;
  phone: string;
  locale: Locale;
  policyAccepted: true;
  policyVersion: typeof BOOKING_POLICY_VERSION;
};

export type ReserveBookingResult = {
  success: true;
  bookingReference: string;
  status: "pending_payment_verification";
  policyVersion: typeof BOOKING_POLICY_VERSION;
  policyAcceptedAt: string;
  duplicate?: boolean;
};

export class BookingReservationError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(code);
    this.name = "BookingReservationError";
  }
}

function isResult(value: unknown): value is ReserveBookingResult {
  if (typeof value !== "object" || value === null) return false;
  const result = value as Partial<ReserveBookingResult>;
  return (
    result.success === true &&
    typeof result.bookingReference === "string" &&
    /^RB-[A-Z0-9]{8}$/.test(result.bookingReference) &&
    result.status === "pending_payment_verification" &&
    result.policyVersion === BOOKING_POLICY_VERSION &&
    typeof result.policyAcceptedAt === "string" &&
    Number.isFinite(Date.parse(result.policyAcceptedAt))
  );
}

export async function reserveBooking(
  input: ReserveBookingInput,
  signal?: AbortSignal,
): Promise<ReserveBookingResult> {
  if (!BOOKING_API_URL || !PUBLISHABLE_KEY) {
    throw new BookingReservationError("booking_configuration_missing", 500);
  }

  const response = await fetch(BOOKING_API_URL, {
    method: "POST",
    headers: {
      apikey: PUBLISHABLE_KEY,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(input),
    signal,
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    throw new BookingReservationError("booking_response_invalid", response.status);
  }

  if (!response.ok) {
    const code =
      typeof payload === "object" &&
      payload !== null &&
      typeof (payload as { error?: unknown }).error === "string"
        ? (payload as { error: string }).error
        : "booking_request_failed";
    throw new BookingReservationError(code, response.status);
  }

  if (!isResult(payload)) {
    throw new BookingReservationError("booking_response_invalid", response.status);
  }
  return payload;
}
